import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  SelectionChangeContext,
  SelectionConfig,
  TablePagination,
  TableSelection,
} from "../types";

type Props<Item> = {
  items: Item[];
  itemsOnCurrentPage: Item[];
  selectable?: false | TableSelection<Item>;
  pagination?: TablePagination;
};

export function useSelectionController<Item>({
  items,
  itemsOnCurrentPage,
  selectable,
  pagination,
}: Props<Item>): SelectionConfig<Item> | undefined {
  const enabled = Boolean(selectable);

  const idKey =
    typeof selectable !== "boolean" ? (selectable?.idKey as keyof Item) : null;
  const onSelectAll =
    typeof selectable !== "boolean" ? selectable?.onSelectAll : undefined;
  const onSelectionChange =
    typeof selectable !== "boolean" ? selectable?.onSelectionChange : undefined;

  const isControlled =
    typeof selectable !== "boolean" && selectable?.selectedItems !== undefined;

  const [internalSelectedItems, setInternalSelectedItems] = useState<Item[]>(
    [],
  );

  const selectedItems = isControlled
    ? (selectable?.selectedItems ?? [])
    : internalSelectedItems;

  const updateSelection = useCallback(
    (newItems: Item[], context: SelectionChangeContext<Item>) => {
      if (isControlled) {
        onSelectionChange?.(newItems, context);
      } else {
        setInternalSelectedItems(newItems);
        onSelectionChange?.(newItems, context);
      }
    },
    [isControlled, onSelectionChange],
  );

  const defaultSelectAll =
    typeof selectable !== "boolean" ? selectable?.selectAll : undefined;

  const isSelectAllControlled = defaultSelectAll !== undefined;
  const [internalSelectAll, setInternalSelectAll] = useState(false);

  const selectAll = isSelectAllControlled
    ? defaultSelectAll
    : internalSelectAll;
  const setSelectAll = isSelectAllControlled ? () => {} : setInternalSelectAll;

  const hasMultiplePages = useMemo(() => {
    if (!pagination) return false;
    const totalPages = Math.ceil(pagination.totalItems / pagination.pageSize);
    return totalPages > 1;
  }, [pagination]);

  const showSelectAllButton = !!onSelectAll && hasMultiplePages;

  const getItemId = useCallback(
    (item: Item) => {
      if (!idKey) {
        throw new Error("getItemId called while disabled");
      }
      return item[idKey];
    },
    [idKey],
  );

  const isItemSelected = useCallback(
    (item: Item): boolean => {
      if (!enabled) {
        return false;
      }
      if (selectAll) {
        return true;
      }
      const itemId = getItemId(item);
      return selectedItems.some((s) => getItemId(s) === itemId);
    },
    [enabled, selectAll, selectedItems, getItemId],
  );

  const anchorIndexRef = useRef<number | null>(null);

  const indexOfOnPage = useCallback(
    (item: Item) => {
      const id = getItemId(item);
      return itemsOnCurrentPage.findIndex((it) => getItemId(it) === id);
    },
    [itemsOnCurrentPage, getItemId],
  );

  const applyRange = useCallback(
    (startIdx: number, endIdx: number, checked: boolean) => {
      const [lo, hi] =
        startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
      const rangeItems = itemsOnCurrentPage.slice(lo, hi + 1);

      let next = selectedItems;
      let affectedItems: Item[];

      if (checked) {
        const existing = new Set(selectedItems.map(getItemId));
        affectedItems = rangeItems.filter((i) => !existing.has(getItemId(i)));
        next = [...selectedItems, ...affectedItems];
      } else {
        const idsToRemove = new Set(rangeItems.map(getItemId));
        affectedItems = selectedItems.filter((i) => {
          return idsToRemove.has(getItemId(i));
        });
        next = selectedItems.filter((i) => !idsToRemove.has(getItemId(i)));
      }

      updateSelection(next, {
        items: affectedItems,
        action: checked ? "select" : "deselect",
      });
    },
    [itemsOnCurrentPage, selectedItems, getItemId, updateSelection],
  );

  const onSelectRow = useCallback(
    (item: Item) => {
      return (checked: boolean, e?: ChangeEvent<HTMLInputElement>) => {
        if (!enabled) {
          return;
        }

        if (
          (e?.nativeEvent as MouseEvent)?.shiftKey &&
          anchorIndexRef.current !== null
        ) {
          const currentIdx = indexOfOnPage(item);
          if (currentIdx !== -1) {
            applyRange(anchorIndexRef.current, currentIdx, checked);
            anchorIndexRef.current = currentIdx;
            return;
          }
        }

        const itemId = getItemId(item);

        if (selectAll) {
          if (!checked) {
            setSelectAll(false);
            const newSelected = itemsOnCurrentPage.filter(
              (p) => getItemId(p) !== itemId,
            );
            updateSelection(newSelected, { items: [item], action: "deselect" });
          }
          anchorIndexRef.current = indexOfOnPage(item);
          return;
        }

        const newSelected = checked
          ? [...selectedItems, item]
          : selectedItems.filter((s) => getItemId(s) !== itemId);

        updateSelection(newSelected, {
          items: [item],
          action: checked ? "select" : "deselect",
        });

        anchorIndexRef.current = indexOfOnPage(item);
      };
    },
    [
      enabled,
      selectAll,
      selectedItems,
      itemsOnCurrentPage,
      getItemId,
      updateSelection,
      indexOfOnPage,
      applyRange,
    ],
  );

  const onSelectAllFromPage = useCallback(
    (checked: boolean) => {
      if (!enabled) return;

      if (selectAll) {
        if (!checked) {
          setSelectAll(false);
          updateSelection([], {
            items: itemsOnCurrentPage,
            action: "deselect",
          });
        }
        return;
      }

      if (!checked) {
        const pageIdSet = new Set(itemsOnCurrentPage.map(getItemId));
        const removed = selectedItems.filter((i) =>
          pageIdSet.has(getItemId(i)),
        );
        const newSelected = selectedItems.filter(
          (i) => !pageIdSet.has(getItemId(i)),
        );
        updateSelection(newSelected, { items: removed, action: "deselect" });
      } else {
        const toAdd = itemsOnCurrentPage.filter((i) => !isItemSelected(i));
        const newSelected = [...selectedItems, ...toAdd];
        updateSelection(newSelected, { items: toAdd, action: "select" });
      }
    },
    [
      enabled,
      selectAll,
      itemsOnCurrentPage,
      selectedItems,
      isItemSelected,
      getItemId,
      updateSelection,
    ],
  );

  const handleSelectAll = useCallback(() => {
    if (!enabled) {
      return;
    }
    setSelectAll(true);
    updateSelection([], { items: selectedItems, action: "deselect" });
    onSelectAll?.();
  }, [enabled, selectedItems, updateSelection, onSelectAll]);

  const allPageItemsSelected = useMemo(() => {
    return (
      enabled &&
      itemsOnCurrentPage.length > 0 &&
      itemsOnCurrentPage.every((i) => isItemSelected(i))
    );
  }, [enabled, itemsOnCurrentPage, isItemSelected]);

  const somePageItemsSelected = useMemo(() => {
    return (
      enabled &&
      itemsOnCurrentPage.some((i) => isItemSelected(i)) &&
      !allPageItemsSelected
    );
  }, [enabled, itemsOnCurrentPage, isItemSelected, allPageItemsSelected]);

  useEffect(() => {
    if (!enabled || isControlled) {
      return;
    }
    setInternalSelectedItems([]);
    setInternalSelectAll(false);
  }, [enabled, isControlled, items]);

  useEffect(() => {
    anchorIndexRef.current = null;
  }, [itemsOnCurrentPage]);

  if (!enabled) {
    return undefined;
  }

  return {
    selectedItems,
    selectAll,
    allPageItemsSelected,
    somePageItemsSelected,
    isItemSelected,
    onSelectRow,
    onSelectAllFromPage,
    showSelectAllButton,
    onSelectAll: showSelectAllButton ? handleSelectAll : undefined,
  };
}
